import './index.scss';
import { Trash } from 'react-feather';
import {
  useCloseOldModal,
  useModalData,
  useOpenOldModal,
  useStartLoader,
  useStopLoader,
} from '../../../../store/modal/modalHooks';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Toast from '../../../shared/Toast/Toast';
import { useTranslation } from 'react-i18next';
import 'cleave.js/dist/addons/cleave-phone.us';
import { useProfile } from '../../../../store/Auth/authHooks';
import {
  useFetchSpacesManagementSpacesByCompany,
  useSpacesManagementCompanySpaces,
} from '../../../../store/SpacesManagement/spacesManagementHooks';
import {
  useRemoveRepeatableGuest,
  useUpdateRepeatableGuest,
} from '../../../../store/guests-management/guestsManagementHooks';
import { ModalComponentEnum } from '../../../../store/modal/modalTypes';
import { Button, Form, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { RepeatableGuest } from '../../../../store/guests-management/guestsManagementTypes';
import { InviteRepeatableVisitorFormValues } from '../../../modals/InviteRepeatableVisitorModal';
import { AddNewRepeatableGuestRequestDto } from '../../../../API/guests/dto/request/add-new-repeatable-guest.request.dto';
import { InviteRepeatableGuestForm } from '../InviteRepeatableGuestForm';

const EditRepeatableGuestModal = () => {
  const profile = useProfile();
  const { t } = useTranslation();
  const openModal = useOpenOldModal();
  const startLoader = useStartLoader();
  const stopLoader = useStopLoader();
  const modalData = useModalData();
  const closeModal = useCloseOldModal();
  const guest: RepeatableGuest = modalData?.data?.guest;
  const updateGuest = useUpdateRepeatableGuest();
  const removeGuest = useRemoveRepeatableGuest();
  const companySpaces = useSpacesManagementCompanySpaces();
  const fetchCompanySpaces = useFetchSpacesManagementSpacesByCompany();

  if (!guest) {
    closeModal();
    return null;
  }
  useEffect(() => {
    fetchCompanySpaces().finally();
  }, [profile?.company?.id!]);

  const spaces = [
    ...new Set(
      companySpaces.map(
        (space) => ` ${space.building.name}, ${space.floor?.number}`
      )
    ),
  ];

  useEffect(() => {
    if (guest) {
      reset({
        fullName: guest.fullName || '',
        phone: guest.phone || '',
        email: guest.email || '',
        spaceId: guest.companySpaceId,
        licenseNumber: guest?.licenseNumber || '',
        startDate: [new Date(+guest.startDate)],
        endDate: [new Date(+guest.endDate)],
        parkingNumber: guest.parkingNumber || '',
        parkingFloorNumber: guest.parkingFloorNumber || '',
      });
    }
  }, [guest, companySpaces]);

  const form = useForm<InviteRepeatableVisitorFormValues>();
  const {
    reset,
    handleSubmit,
    formState: { errors },
  } = form;

  const onDeleteGuest = () => {
    openModal(ModalComponentEnum.QUESTION_REMOVE_GUEST_MODAL, {}, {}, () => {
      removeGuest(guest.id)
        .then((response) => {
          closeModal();
          Toast('success-request', t('VISITOR_HAS_BEEN_DELETED'), t);
        })
        .catch((error) => {
          Toast('error', error.response.data.error.message);
        });
    });
  };

  const onSubmit = async (data: InviteRepeatableVisitorFormValues) => {
    await startLoader();
    const newGuest: AddNewRepeatableGuestRequestDto = {
      email: data.email,
      company_space_id: data.spaceId,
      phone: data.phone,
      full_name: data.fullName,
      start_date: new Date(data.startDate[0]).getTime(),
      end_date: new Date(data.endDate[0]).getTime(),
    };
    try {
      await updateGuest(guest.id, newGuest);
      Toast(
        'success-request',
        t('VISITOR_UPDATED_SUCCESSFULLY', { id: guest.id })
      );
      closeModal();
    } catch (error: any) {
      Toast(
        'error',
        error.response?.data?.error?.message || 'Failed to update visitor'
      );
    } finally {
      await stopLoader();
    }
  };

  return (
    <div className="invite-new-guest-modal">
      <div className="form-modal">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <p className="form-header-title">{t('EDIT_REPEATABLE_VISITOR')}</p>
          </ModalHeader>
          <ModalBody>
            <InviteRepeatableGuestForm form={form} />
          </ModalBody>
          <ModalFooter className="update-guest-buttons">
            <div>
              <Button className="delete-guest" color="" onClick={onDeleteGuest}>
                <Trash size={10} />
                {t('DELETE_VISITOR')}
              </Button>
            </div>
            <div>
              <Button type="button" color="" onClick={closeModal}>
                {t('CLOSE_BUTTON')}
              </Button>{' '}
              <Button type="submit" color="primary">
                {t('SAVE_CHANGES')}
              </Button>{' '}
            </div>
          </ModalFooter>
        </Form>
      </div>
    </div>
  );
};

export default EditRepeatableGuestModal;
